import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { apiGet } from '@/api/client';
import type { AuditRow } from '@/api/types';
import { AsyncSection } from '@/app/components/AsyncSection';
import { Button } from '@/app/components/ui/Button';
import { DataTable } from '@/app/components/ui/DataTable';
import { EmptyState } from '@/app/components/ui/EmptyState';
import { Field, Input } from '@/app/components/ui/Field';
import { PageHeader } from '@/app/components/ui/PageHeader';
import { useMe } from '@/features/me/MeContext';
import { useAsync } from '@/lib/useAsync';

/**
 * The audit trail. An admin reads everything; a company admin gets the API's
 * allowlisted subset for their companies (consent answers and identity links
 * never appear there). The filters go to the server — this page shows what
 * the API decides the caller may see.
 */
export function AuditPage() {
  const { t } = useTranslation();
  const { permissions } = useMe();
  const [action, setAction] = useState('');
  const [scopeId, setScopeId] = useState('');
  const [applied, setApplied] = useState<{ action: string; scope_id: string }>({
    action: '',
    scope_id: '',
  });
  const rows = useAsync(
    () =>
      apiGet('/audit', {
        query: {
          ...(applied.action !== '' ? { action: applied.action } : {}),
          ...(applied.scope_id !== '' ? { scope_id: applied.scope_id } : {}),
        },
      }) as Promise<AuditRow[]>,
    [applied],
  );

  return (
    <section className="page flex flex-col gap-6">
      <PageHeader
        title={t('audit.page.title')}
        lead={!permissions.canViewFullAudit ? t('audit.page.scopedLead') : undefined}
      />

      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          setApplied({ action, scope_id: scopeId });
        }}
      >
        <Field label={t('audit.page.actionFilter')} className="w-64">
          <Input
            value={action}
            onChange={(e) => setAction(e.target.value)}
            placeholder={t('audit.page.actionPlaceholder')}
          />
        </Field>
        <Field label={t('audit.page.scopeFilter')} className="w-48">
          <Input
            value={scopeId}
            onChange={(e) => setScopeId(e.target.value)}
            placeholder={t('audit.page.scopePlaceholder')}
          />
        </Field>
        <Button type="submit">{t('audit.page.filter')}</Button>
      </form>

      <AsyncSection state={rows} label={t('audit.page.rowsLabel')} skeleton="table">
        {(data) =>
          data.length === 0 ? (
            <EmptyState text={t('audit.page.empty')} />
          ) : (
            <DataTable
              rowKey={(row) => row.id ?? ''}
              rows={data}
              columns={[
                { key: 'when', header: t('audit.page.when'), render: (row) => row.created_at },
                { key: 'action', header: t('audit.page.action'), render: (row) => row.action },
                {
                  key: 'actorRole',
                  header: t('audit.page.actorRole'),
                  render: (row) => row.actor_role,
                },
                {
                  key: 'target',
                  header: t('audit.page.target'),
                  render: (row) => `${row.target_type ?? ''} ${row.target_id ?? ''}`,
                },
              ]}
            />
          )
        }
      </AsyncSection>
    </section>
  );
}
